import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiClient } from "../../api/apiClient";

export interface Level {
  id: string;
  order: string;
  label: string;
  description?: string;
  color: string;
}

interface LevelState {
  levels: Level[];
  loading: boolean;
  error: string | null;
}

const initialState: LevelState = {
  levels: [],
  loading: false,
  error: null,
};

export const fetchLevelsAsync = createAsyncThunk(
  "levels/fetchLevels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/levels");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch levels",
      );
    }
  },
);

export const addLevelAsync = createAsyncThunk(
  "levels/addLevel",
  async (levelData: Omit<Level, "id">, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/levels", levelData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || error.message || "Failed to add level",
      );
    }
  },
);

export const updateLevelAsync = createAsyncThunk(
  "levels/updateLevel",
  async (levelData: Level, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/levels/${levelData.id}`, levelData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || error.message || "Failed to update level",
      );
    }
  },
);

export const deleteLevelAsync = createAsyncThunk(
  "levels/deleteLevel",
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/levels/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete level",
      );
    }
  },
);

const levelSlice = createSlice({
  name: "levels",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLevelsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLevelsAsync.fulfilled, (state, action: PayloadAction<Level[]>) => {
        state.loading = false;
        state.levels = action.payload;
      })
      .addCase(fetchLevelsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addLevelAsync.fulfilled, (state, action: PayloadAction<Level>) => {
        state.levels.push(action.payload);
        state.levels.sort((a, b) => {
            const aNum = parseInt(a.order.replace(/[^0-9]/g, '')) || 999;
            const bNum = parseInt(b.order.replace(/[^0-9]/g, '')) || 999;
            return aNum - bNum;
        });
      })
      .addCase(updateLevelAsync.fulfilled, (state, action: PayloadAction<Level>) => {
        const index = state.levels.findIndex((l) => l.id === action.payload.id);
        if (index !== -1) {
          state.levels[index] = action.payload;
        }
        state.levels.sort((a, b) => {
            const aNum = parseInt(a.order.replace(/[^0-9]/g, '')) || 999;
            const bNum = parseInt(b.order.replace(/[^0-9]/g, '')) || 999;
            return aNum - bNum;
        });
      })
      .addCase(deleteLevelAsync.fulfilled, (state, action: PayloadAction<string>) => {
        state.levels = state.levels.filter((l) => l.id !== action.payload);
      });
  },
});

export default levelSlice.reducer;
